import {PropsWithChildren, useEffect, useMemo} from "react";
import {useScopedCollection} from "./useScopedCollection";
import {collection, DocumentData, query, QueryConstraint, where} from 'firebase/firestore';
import {appFirestore} from "../index";
import {useAppDispatch, useAppSelector} from "../app/hooks";
import {firebaseDateToDate} from "../app/tools";
import {setCalendar} from "../features/AppDataSlice";
import {selectDepartment} from "../features/AppStateSlice";
import {RRCalendarDaysType, RRCalendarDayType} from "../app/types/RRCalendarDayType";
import moment from "moment";

export const calendarDayDataConverter = (value: DocumentData): RRCalendarDayType => {
    return {
        id: value.id,
        ref: value.ref,
        eventId: value.get("eventId"),
        employeeId: value.get("employeeId"),
        employeeName: value.get("employeeName"),
        dayType: value.get("dayType"),
        eventDayStart: firebaseDateToDate(value.get("eventDayStart")),
        eventDayEnd: firebaseDateToDate(value.get("eventDayEnd")),
        restTime: value.get("restTime") || 0,
        bonus: value.get("bonus") || 0,
        description: value.get("description"),
        isHoliday: value.get("isHoliday"),
        isWarehouse: value.get("isWarehouse"),
        isSupervisor: value.get("isSupervisor"),
        isMontage: value.get("isMontage"),
        face: value.get("face"),
        notes: value.get("notes"),
        department: value.get("department") || "montage",
        isConfirmed: value.get("isConfirmed") || false,
    } as RRCalendarDayType;
}

export const calendarDataConverter = (values: DocumentData[]): RRCalendarDaysType => {
    if(!values) return [];

    return values.map(
        value => calendarDayDataConverter(value)
    )
}

export interface Props{
    firstDate: Date;
    daysCount: number;
}
export const FirebaseCalendarLoader: React.FC<PropsWithChildren<Props>> = (props) => {
    const dispatch = useAppDispatch();
    const activeDepartment = useAppSelector(selectDepartment);

    const firstDate = useMemo(
        () => moment(props.firstDate).startOf("day").toDate(),
        [props.firstDate]
    );
    const lastDate = useMemo(
        () => moment(props.firstDate).add(props.daysCount, "days").endOf("day").toDate(),
        [props.firstDate, props.daysCount]
    );
    const constraints = useMemo<QueryConstraint[]>(
        () => (activeDepartment ? [
            where("eventDayStart", ">=", firstDate),
            where("eventDayStart", "<=", lastDate),
            where("department", "==", activeDepartment),
        ] : []),
        [activeDepartment, firstDate, lastDate]
    );


    const calendarQuery = useMemo(
        () => (activeDepartment
            ? query(collection(appFirestore, 'calendar'), ...constraints)
            : null),
        [activeDepartment, constraints]
    );
    const scope = `${activeDepartment}:${firstDate.getTime()}:${lastDate.getTime()}`;
    const {data, loading, error, subscription} = useScopedCollection(calendarQuery, scope);
    useEffect(() => {
        // Празният резултат, зареждането и грешката също трябва да достигат до календара.
        dispatch(setCalendar({
            loading, error: error?.message, data: data ? calendarDataConverter(data.docs) : [],
        }));
    }, [dispatch, data, loading, error]);

    return <>{subscription}{props.children}</>;
}
